import { useCallback, useEffect, useMemo } from 'react'
import {
    useInfiniteQuery,
    useMutation,
    useQueryClient,
    type InfiniteData,
} from '@tanstack/react-query'
import { Agent } from '@atproto/api'
import { useAuth } from '@/hooks/useAuth'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'
import {
    DraftAlreadyDeletedError,
    DraftChangedElsewhereError,
    addPendingCleanupId,
    checkDraftFreshness,
    createDraft as createDraftRequest,
    deleteDraft as deleteDraftRequest,
    fetchDraftsPage,
    pruneOwnedRegistry,
    readPendingCleanupIds,
    removeOwnedDraftId,
    removePendingCleanupId,
    updateDraft as updateDraftRequest,
    type BskyDraftsPage,
} from '@/services/bskyDraftsService'
import type { BskyDraft, BskyDraftInput } from '@/types/drafts'

export interface SaveDraftArgs {
    input: BskyDraftInput
    /** Present when updating an existing Mackinac-owned draft; absent when creating. */
    existingId?: string
    /** The `updatedAt` that was current when the user was last shown the draft. */
    knownUpdatedAt?: string
}

export type CleanupResult = 'deleted' | 'skipped-changed' | 'failed'

const CLEANUP_RETRY_DELAY_MS = 750

function delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms))
}

/**
 * Manages the authenticated user's Bluesky drafts via `app.bsky.draft.*`.
 *
 * Stash is shared state — the same user may be editing on bsky.app or a phone — so this hook
 * never caches the list: `staleTime: 0` plus refetch on focus, reconnect, and an explicit
 * refetch when the drafts dialog opens.
 */
export interface UseBskyDraftsOptions {
    /**
     * Whether the drafts dialog is open. Page 1 is fetched when the dialog opens, not on
     * composer mount — every row can trigger quote and link hydration, so an unopened list
     * must not pay for them.
     */
    listOpen?: boolean
}

export function useBskyDrafts({ listOpen = false }: UseBskyDraftsOptions = {}) {
    const { session } = useAuth()
    const { flags } = useFeatureFlags()
    const queryClient = useQueryClient()
    const agent = useMemo(() => (session ? new Agent(session) : null), [session])
    const did = session?.did

    const isEnabled = !!agent && !!did && !!flags.bskyDrafts
    const queryKey = useMemo(() => ['bskyDrafts', did] as const, [did])

    const query = useInfiniteQuery<BskyDraftsPage, Error>({
        queryKey,
        queryFn: ({ pageParam }) => fetchDraftsPage(agent!, did!, pageParam as string | undefined),
        enabled: isEnabled && listOpen,
        initialPageParam: undefined,
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- an empty cursor string means "no next page", same as missing
        getNextPageParam: last => last.cursor || undefined,
        // Cross-device freshness: never serve a stale draft list.
        staleTime: 0,
        refetchOnWindowFocus: true,
        refetchOnReconnect: true,
        retry: 1,
    })

    const drafts: BskyDraft[] = useMemo(
        // Server order is preserved deliberately — no client-side sort.
        () => query.data?.pages.flatMap(page => page.drafts) ?? [],
        [query.data]
    )

    // Prune the ownership registry only after a successful FULL-list read, so a transient
    // network failure or a partially-paged list can never silently strip ownership.
    const { isSuccess, hasNextPage, isFetching } = query
    useEffect(() => {
        if (!did || !isSuccess || hasNextPage || isFetching) return
        pruneOwnedRegistry(
            did,
            drafts.map(d => d.id)
        )
    }, [did, isSuccess, hasNextPage, isFetching, drafts])

    const invalidate = useCallback(() => {
        // Always invalidate ALL pages. Never hand-patch a page array as the durable state.
        void queryClient.invalidateQueries({ queryKey })
    }, [queryClient, queryKey])

    const saveMutation = useMutation<string, Error, SaveDraftArgs>({
        mutationFn: async ({ input, existingId, knownUpdatedAt }) => {
            if (!agent || !did) throw new Error('Not authenticated')
            if (!flags.bskyDrafts) throw new Error('Drafts feature is disabled')
            if (!existingId) {
                return createDraftRequest(agent, did, input)
            }
            // Pre-mutation freshness check. Throws on network failure so we never proceed
            // blind on a stale classification.
            const freshness = knownUpdatedAt
                ? await checkDraftFreshness(agent, existingId, knownUpdatedAt)
                : 'fresh'
            if (freshness === 'changed') throw new DraftChangedElsewhereError(existingId)
            if (freshness === 'missing') {
                removeOwnedDraftId(did, existingId)
                throw new DraftAlreadyDeletedError(existingId)
            }
            await updateDraftRequest(agent, existingId, input)
            return existingId
        },
        onSettled: invalidate,
    })

    const deleteMutation = useMutation<
        string,
        Error,
        { id: string; knownUpdatedAt?: string },
        { removed: { pageCursor?: string; index: number; draft: BskyDraft } | null }
    >({
        mutationFn: async ({ id, knownUpdatedAt }) => {
            if (!agent || !did) throw new Error('Not authenticated')
            const freshness = knownUpdatedAt
                ? await checkDraftFreshness(agent, id, knownUpdatedAt)
                : 'fresh'
            if (freshness === 'changed') throw new DraftChangedElsewhereError(id)
            if (freshness === 'missing') {
                removeOwnedDraftId(did, id)
                throw new DraftAlreadyDeletedError(id)
            }
            await deleteDraftRequest(agent, did, id)
            return id
        },
        onMutate: async ({ id }) => {
            // Cancel first, so an in-flight refetch cannot land between snapshot and rollback.
            await queryClient.cancelQueries({ queryKey })
            const data = queryClient.getQueryData<InfiniteData<BskyDraftsPage>>(queryKey)
            let removed: { pageCursor?: string; index: number; draft: BskyDraft } | null = null
            for (const page of data?.pages ?? []) {
                const index = page.drafts.findIndex(d => d.id === id)
                if (index >= 0) {
                    removed = { pageCursor: page.cursor, index, draft: page.drafts[index] }
                    break
                }
            }
            // Optimistically remove ONLY the affected item.
            queryClient.setQueryData<InfiniteData<BskyDraftsPage>>(queryKey, old =>
                old
                    ? {
                          ...old,
                          pages: old.pages.map(page => ({
                              ...page,
                              drafts: page.drafts.filter(d => d.id !== id),
                          })),
                      }
                    : old
            )
            return { removed }
        },
        onError: (_err, _vars, context) => {
            const removed = context?.removed
            if (!removed) return
            // Restore ONLY that item, into the page with the same stable cursor identity —
            // never by a raw array index captured against a since-changed page list.
            //
            // This is a UI courtesy to avoid a flash, not a state-reconciliation mechanism:
            // the mandatory refetch in onSettled is the source of truth, so two overlapping
            // deletes converge via the server rather than via snapshot arithmetic.
            queryClient.setQueryData<InfiniteData<BskyDraftsPage>>(queryKey, old => {
                if (!old) return old
                const pageIndex = old.pages.findIndex(p => p.cursor === removed.pageCursor)
                if (pageIndex < 0) return old
                const page = old.pages[pageIndex]
                if (page.drafts.some(d => d.id === removed.draft.id)) return old
                const restored = [...page.drafts]
                restored.splice(Math.min(removed.index, restored.length), 0, removed.draft)
                const pages = [...old.pages]
                pages[pageIndex] = { ...page, drafts: restored }
                return { ...old, pages }
            })
        },
        onSettled: invalidate,
    })

    /**
     * Delete the draft a published post came from.
     *
     * Durable by design: the id is persisted BEFORE the attempt, so a tab closed mid-flight
     * leaves a marker that `retryPendingCleanup` picks up on the next mount.
     */
    const cleanupPublishedDraft = useCallback(
        async (id: string, knownUpdatedAt?: string): Promise<CleanupResult> => {
            if (!agent || !did) return 'failed'
            addPendingCleanupId(did, id, knownUpdatedAt)
            try {
                const freshness = knownUpdatedAt
                    ? await checkDraftFreshness(agent, id, knownUpdatedAt)
                    : 'fresh'
                if (freshness === 'changed') {
                    // Changed elsewhere — skip rather than clobber another client's edit.
                    removePendingCleanupId(did, id)
                    return 'skipped-changed'
                }
                if (freshness === 'missing') {
                    removeOwnedDraftId(did, id)
                    removePendingCleanupId(did, id)
                    return 'deleted'
                }
            } catch {
                // Freshness lookup failed — fall through and attempt the delete anyway rather
                // than orphaning the draft; the retry below covers a transient blip.
            }

            for (let attempt = 0; attempt < 2; attempt++) {
                try {
                    await deleteDraftRequest(agent, did, id)
                    removePendingCleanupId(did, id)
                    return 'deleted'
                } catch {
                    if (attempt === 0) await delay(CLEANUP_RETRY_DELAY_MS)
                }
            }
            // Retries exhausted. The pending marker AND the ownership-registry entry are both
            // kept: the registry entry is what makes the stale row deletable from the drafts
            // list, which the failure toast tells the user to do.
            return 'failed'
        },
        [agent, did]
    )

    // Retry cleanup entries left behind by a tab that was closed mid-delete. One attempt per
    // mount — never a loop. A still-failing entry keeps its marker for the next mount.
    useEffect(() => {
        if (!isEnabled || !agent || !did) return
        const pending = readPendingCleanupIds(did)
        if (pending.length === 0) return
        let cancelled = false
        void (async () => {
            let anyDeleted = false
            for (const { id, updatedAt } of pending) {
                if (cancelled) return
                try {
                    // Same freshness check cleanupPublishedDraft's first attempt runs — a
                    // remounted tab must not delete an unconditionally stale draft just
                    // because the check was skipped on the original attempt.
                    const freshness = updatedAt
                        ? await checkDraftFreshness(agent, id, updatedAt)
                        : 'fresh'
                    if (freshness === 'changed') {
                        // Changed elsewhere — skip rather than clobber another client's edit.
                        removePendingCleanupId(did, id)
                        continue
                    }
                    if (freshness === 'missing') {
                        removeOwnedDraftId(did, id)
                        removePendingCleanupId(did, id)
                        anyDeleted = true
                        continue
                    }
                } catch {
                    // Freshness lookup failed — fall through and attempt the delete anyway
                    // rather than orphaning the draft, matching cleanupPublishedDraft's first
                    // attempt.
                }
                try {
                    await deleteDraftRequest(agent, did, id)
                    removePendingCleanupId(did, id)
                    anyDeleted = true
                } catch {
                    // Leave it for the user to delete manually from the drafts list.
                }
            }
            if (!cancelled && anyDeleted) invalidate()
        })()
        return () => {
            cancelled = true
        }
    }, [isEnabled, agent, did, invalidate])

    return {
        agent,
        did,
        drafts,
        isLoading: query.isLoading,
        isError: query.isError,
        hasNextPage: query.hasNextPage,
        isFetchingNextPage: query.isFetchingNextPage,
        fetchNextPage: query.fetchNextPage,
        refetch: query.refetch,
        saveDraft: (args: SaveDraftArgs) => saveMutation.mutateAsync(args),
        deleteDraft: (id: string, knownUpdatedAt?: string) =>
            deleteMutation.mutateAsync({ id, knownUpdatedAt }),
        cleanupPublishedDraft,
        isSaving: saveMutation.isPending,
        isDeleting: deleteMutation.isPending,
    }
}
